import React, { useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import Box, { Box as LoadingContainer } from '@node-space/storybook-components/dist/Box'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import { ModalNavBar, ModalScrollable } from '@node-space/storybook-components/dist/Modal'
import { Avatar, Button, Combobox, Box as FormButtons, Input } from '@node-space/ui-components'
import loadingAnimation from 'components/spinners/LoadingAnimationDark'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import useBeneficiaryPayoutDisclaimer from 'hooks/useBeneficiaryPayoutDisclaimer'
import { BaseErrorResponse, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import {
  formatString,
  generateReference,
  isTestEnvironment,
  roundNumberWithCommas,
  stringToDecimal,
} from 'utils/utils'
import { getWalletPropertiesForCapabilityChecks } from 'utils/wallet'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { getWithdrawRequest } from 'utils/withdraw'
import { selectedFiatBeneficiaryAtom } from '../Pay/atoms/atoms'
import useBeneficiaryOptions from '../Pay/hooks/useBeneficiaryOptions'
import { WithdrawActionProps } from './Withdraw'

const WithdrawForm = ({
  loading,
  beneficiaries,
  form,
  isBeneficiaryListError,
  wallets,
  dismissAction,
  registerFormField,
  setStep,
}: WithdrawActionProps) => {
  const { t } = useTranslation()
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false)
  const setSelectedFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const { enableFiatCurrencyFilter } = useFeatureFlags()
  const formValues = form.watch()

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!wallets?.length) {
      const walletLsids = wallets?.map(wallet => wallet?.lsid)

      return getWalletActionCapabilityRequestFilters(walletLsids)
    }
  }, [wallets])

  const { data: bulkCapabilities, isFetching: isFetchingCapabilities } = useBulkCapabiltiesQuery(
    fiatWalletCapabilitiesRequest,
    !!fiatWalletCapabilitiesRequest
  )

  const supportedWallets = useMemo(() => {
    return wallets.filter(wallet => {
      const walletProperties = getWalletPropertiesForCapabilityChecks(wallet)

      const { supportsWithdrawals } = getFiatWalletActionSupport({
        walletProperties,
        bulkCapabilities,
        enableFiatCurrencyFilter,
      })

      return wallet?.currency?.code && wallet?.currency?.fiat && supportsWithdrawals
    })
  }, [wallets, bulkCapabilities])

  const selectedWallet = useMemo(() => {
    return (
      formValues?.walletId &&
      supportedWallets?.find(x => x?.id?.toString() === formValues?.walletId)
    )
  }, [formValues?.walletId, supportedWallets])

  const selectedBeneficiary =
    formValues?.bankAccountId &&
    beneficiaries?.find(beneficiary => {
      return beneficiary?.reference === formValues?.bankAccountId
    })

  const isLegacyPaymentMethod = !isNaN(Number(selectedBeneficiary?.reference))
  const isPayoutsV2Enabled =
    !isLegacyPaymentMethod && !!selectedWallet?.supportedTransferDestinations?.length
  const request = getWithdrawRequest(formValues, selectedWallet, isPayoutsV2Enabled)
  const payoutFeeEstimateParams: PayoutsFeeEstimateParams = {
    request,
    isEnabled: false,
    isThirdPartyPayment: false,
    isPayoutFeesV2: isPayoutsV2Enabled,
    transferDestination:
      selectedBeneficiary?.transferDestination ?? BeneficiaryTransferDestination.LOCAL,
  }

  const { isFetching: isFetchingFee, refetch: fetchFee } =
    usePayoutsFeeEstimateQuery(payoutFeeEstimateParams)

  useEffect(() => {
    const beneficiary = beneficiaries?.find(
      beneficiary => beneficiary?.reference === formValues?.bankAccountId
    )
    setSelectedFiatBeneficiaryAtom(beneficiary)

    if (!beneficiary) {
      form.setValue('reference', '')
    } else if (beneficiary) {
      form.setValue('reference', beneficiary?.beneficiaryPaymentReference ?? generateReference())
      form.setValue('currencyCode', beneficiary?.currency)
      form.setValue('paymentMethod', beneficiary?.paymentMethod)
      form.setValue('transferDestination', beneficiary?.transferDestination)
    }
  }, [formValues?.bankAccountId])

  const isZeroBalanceState = useMemo(() => {
    return selectedWallet?.balance === 0 || isInsufficientBalance
  }, [isInsufficientBalance, selectedWallet?.balance])

  const sourceWallets = useMemo(() => {
    return supportedWallets.map(wallet => ({
      icon: wallet?.currency?.code?.toLowerCase(),
      walletIcon: true,
      value: wallet?.id?.toString(),
      label: wallet?.description,
      secondLabel: formatString(
        t('balanceWithCurrency'),
        roundNumberWithCommas(wallet.balance, 2),
        wallet?.currency?.code
      ),
    }))
  }, [supportedWallets])

  const walletOptions = useMemo(() => {
    return supportedWallets?.map(wallet => {
      return {
        LeftElement: <Avatar size="small" type="currency" name={wallet?.currency?.code} />,
        leftText: wallet?.description,
        rightText: `${roundNumberWithCommas(wallet?.balance, 2)}
            ${wallet?.currency?.code}`,
        value: wallet?.id?.toString(),
      }
    })
  }, [supportedWallets])

  const filteredBeneficiaryList = useMemo(() => {
    return beneficiaries?.filter(
      beneficiary => beneficiary?.currency === selectedWallet?.currency?.code
    )
  }, [beneficiaries, selectedWallet?.currency?.code])

  const beneficiaryOptions = useBeneficiaryOptions({
    beneficiaries: filteredBeneficiaryList,
    walletTransferDestinations: selectedWallet?.supportedTransferDestinations,
  })

  const isNoSupportedBeneficiaries = !!beneficiaries?.length && !beneficiaryOptions?.length

  const selectBeneficiaryDropdownPlaceholder = useMemo(() => {
    if (beneficiaries?.length && !beneficiaryOptions?.length) {
      return t('manageBeneficiaries.beneficiarySelect')
    } else if (!beneficiaries?.length && !beneficiaryOptions?.length) {
      return t('manageBeneficiaries.noBeneficiaries')
    } else if (!!beneficiaryOptions?.length && !formValues?.bankAccountId) {
      return t('manageBeneficiaries.beneficiarySelect')
    }
  }, [beneficiaryOptions, formValues?.bankAccountId, beneficiaries])

  const canContinue = useMemo(() => {
    return (
      !loading &&
      !!formValues?.bankAccountId &&
      !!formValues?.amount &&
      !isInsufficientBalance &&
      !isBeneficiaryListError &&
      !form.formState.errors.amount?.message
    )
  }, [
    formValues.bankAccountId,
    formValues.amount,
    form.formState.errors.amount,
    loading,
    isInsufficientBalance,
    isBeneficiaryListError,
    form.formState.errors.amount?.message,
  ])

  const sourceWalletPlaceholder = useMemo(() => {
    if (!sourceWallets) {
      return t('loadingEllipses')
    } else if (sourceWallets?.length === 0) {
      return t('noWallets')
    } else {
      return t('searchOrSelectAWallet')
    }
  }, [sourceWallets])

  useEffect(() => {
    if (!formValues?.reference) {
      form?.setValue('reference', generateReference())
    }
  }, [])

  const beneficiaryListHelperText = useBeneficiaryPayoutDisclaimer(
    !!beneficiaries?.length,
    !!beneficiaryOptions?.length
  )

  const validateAmount = async (amount: number | string, onBlur = isTestEnvironment) => {
    const error = await checkMaxAmount(amount)

    if (error) {
      form.setError('amount', { message: error })
    } else {
      form.clearErrors('amount')
    }
  }

  const checkMaxAmount = async (amount: number | string) => {
    const feeData = await fetchFee()
    const { maximumWithdrawalAmount } = feeData?.data || {}

    setIsInsufficientBalance(selectedWallet?.balance < maximumWithdrawalAmount)
    const amountErrorMaximumFees =
      feeData &&
      selectedWallet &&
      formatString(
        t('amountErrorMaximumFees'),
        roundNumberWithCommas(maximumWithdrawalAmount),
        selectedWallet?.currency?.code
      )

    // check amount against the maximum amount
    if (Number(amount) > maximumWithdrawalAmount) {
      return amountErrorMaximumFees
    }
  }

  const amountOnBlurHandler = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    validateAmount(stringToDecimal(event?.target?.value), true)
  }

  const amountOnChangeHandler = e => {
    form?.setValue('amount', e.currentTarget.value)
  }

  const onSubmit = async () => {
    form?.clearErrors()
    const isValid = await form?.trigger()

    if (isValid) {
      const { data: feeData, error: withdrawError } = await fetchFee()
      const { maximumWithdrawalAmount, currencyCode, fee } = feeData || {}

      if (!!withdrawError) {
        const errorMessage = (withdrawError as BaseErrorResponse)?.data?.message
        form.setError('amount', { message: errorMessage || t('fetchingFeeError') })
        return
      }

      if (Number(formValues?.amount) > maximumWithdrawalAmount) {
        form.setError('amount', {
          message: formatString(
            t('amountErrorMaximumFees'),
            roundNumberWithCommas(maximumWithdrawalAmount),
            currencyCode
          ),
        })
        return
      }

      form.setValue('withdrawalFee', fee)
      setStep('confirm')
    }
  }

  return (
    <Box>
      <ModalNavBar title={t('withdraw')} onClose={dismissAction} />
      <ModalScrollable>
        <Box flex direction="col" padding={24} paddingT={20} gapY={24}>
          <Combobox
            id={t('selectWallet')}
            label={t('formFields.withdrawFromWallet')}
            items={walletOptions}
            defaultValue={selectedWallet ? formValues.walletId : null}
            loadingInline={!selectedWallet || !formValues.walletId || isFetchingCapabilities}
            onChange={(value: string) =>
              form.reset({ walletId: value }, { keepDefaultValues: true })
            }
            selectPlaceholder={sourceWalletPlaceholder}
          />

          {(loading || !selectedWallet) && (
            <LoadingContainer flex justifyContent="center" paddingT={32}>
              {loadingAnimation}
            </LoadingContainer>
          )}

          {!loading && selectedWallet && (
            <>
              {isZeroBalanceState && (
                <Callout
                  state="error"
                  message={t('withdrawInsufficient', {
                    currencyCode: selectedWallet?.currency?.code,
                  })}
                  testid="withdraw-insufficent-balance"
                />
              )}

              {isBeneficiaryListError && (
                <Callout state="error" message={t('manageBeneficiaries.beneficiaryFetchError')} />
              )}

              {isNoSupportedBeneficiaries && !isZeroBalanceState && (
                <Callout state="warning" message={t('payouts.noSupportedBeneficiaries')} />
              )}

              {!isZeroBalanceState && !isNoSupportedBeneficiaries && (
                <>
                  <Combobox
                    testId="beneficiary-options"
                    id="beneficiary-options"
                    label={t('manageBeneficiaries.selectBeneficiary')}
                    items={beneficiaryOptions}
                    defaultValue={formValues?.bankAccountId}
                    onChange={(value: string) => form.setValue('bankAccountId', value)}
                    helperText={beneficiaryListHelperText}
                    selectPlaceholder={selectBeneficiaryDropdownPlaceholder}
                    loading={loading}
                  />

                  <Input
                    {...registerFormField('amount')}
                    label={t('amount')}
                    testId="amount"
                    rightSuffixText={selectedWallet?.currency?.code}
                    type="text"
                    inputMode="numeric"
                    required
                    disabled={!formValues.bankAccountId}
                    onBlur={amountOnBlurHandler}
                    onChange={amountOnChangeHandler}
                    error={!!form?.formState?.errors?.amount?.message}
                    errorText={form?.formState?.errors?.amount?.message}
                  />

                  <Input
                    {...registerFormField('reference')}
                    placeholder={t('inputPlaceholders.beneficiaryPaymentReferencePlaceHolder')}
                    value={formValues?.reference}
                    onChange={e => form.setValue('reference', e.target.value)}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </ModalScrollable>

      {/* actions */}
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormButtons
          width="full"
          paddingY={16}
          paddingX={20}
          flex
          gapX={12}
          justify="end"
          className="border-t border-gray-200"
        >
          <Button variant="secondary" onClick={dismissAction}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            testId={t('withdrawPreview')}
            type="submit"
            loading={isFetchingFee || loading}
            disabled={!canContinue}
          >
            {t('withdrawPreview')}
          </Button>
        </FormButtons>
      </form>
    </Box>
  )
}

export default WithdrawForm
